/**
 * What the guide can still say with no network (#43, US-6.1 AC2) — read from a
 * dated snapshot of the Agent's own data, never from a guess.
 *
 * `snapshot.json` is GENERATED by `ci/offline_snapshot.py` from the same files the
 * Agent's blocks read (map catalogue, closures, the Lower Park's hours by period,
 * the comfort reference) and pinned by `tests/test_offline_snapshot.py`. Every part
 * carries its own date and the panel prints it: offline the visitor reads a
 * snapshot, not today's feed. Distances are straight lines, like the Agent's
 * «РЯДОМ» block — a canal or a fence can stand between two close points.
 */

import snapshot from './snapshot.json';

export type Place = { id: number; title: string; lat: number; lon: number };
export type Near = { place: Place; metres: number; bearing: string };
type Dated = { from?: string | null; until?: string | null };

export const OFFLINE = snapshot;

/** Today in Moscow — the museum's date, never the device's zone. */
export function museumDay(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' }).format(now);
}

/** Rows valid on `day` (ISO), by their optional `from`/`until` — the same rule as the Agent's reference. */
export function activeOn<T extends Dated>(rows: readonly T[], day: string): T[] {
  return rows.filter((row) => (!row.from || row.from <= day) && (!row.until || day <= row.until));
}

/** A closure record past its `until` says nothing — it is not «open» either, it is just not shown. */
export function closuresOn(day: string) {
  return OFFLINE.closures.filter((row) => !row.until || day <= row.until);
}

export function hoursOn(day: string) {
  return activeOn(OFFLINE.hours, day);
}

export function comfortOn(day: string) {
  return activeOn(OFFLINE.comfort.lines, day);
}

/** Metres between two points — the equirectangular projection the Agent uses (`route.distance_m`). */
export function straightMetres(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const lat = (((a.lat + b.lat) / 2) * Math.PI) / 180;
  const dx = (((b.lon - a.lon) * Math.PI) / 180) * Math.cos(lat);
  const dy = ((b.lat - a.lat) * Math.PI) / 180;
  return 6_371_000 * Math.hypot(dx, dy);
}

const COMPASS = ['к северу', 'к северо-востоку', 'к востоку', 'к юго-востоку', 'к югу', 'к юго-западу', 'к западу', 'к северо-западу'];

export function bearing(from: { lat: number; lon: number }, to: { lat: number; lon: number }): string {
  const north = to.lat - from.lat;
  const east = (to.lon - from.lon) * Math.cos((from.lat * Math.PI) / 180);
  const degrees = ((Math.atan2(east, north) * 180) / Math.PI + 360) % 360;
  return COMPASS[Math.round(degrees / 45) % 8]!;
}

/** The `count` nearest places within `radius` metres, rounded to 10 m like the Agent's block. */
export function nearest(
  places: readonly Place[],
  here: { lat: number; lon: number },
  count = 3,
  radius = 2000,
): Near[] {
  return places
    .map((place) => ({ place, metres: straightMetres(here, place) }))
    .filter((row) => row.metres <= radius)
    .sort((a, b) => a.metres - b.metres)
    .slice(0, count)
    .map((row) => ({ place: row.place, metres: Math.max(10, Math.round(row.metres / 10) * 10), bearing: bearing(here, row.place) }));
}

/** «02.10.2026» from an ISO date — the snapshot's dates are printed, not parsed by the reader. */
export function ruDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  return year && month && day ? `${day}.${month}.${year}` : iso;
}
